import React, { useState, MouseEvent, useMemo, useCallback } from 'react'
import { Trans } from 'react-i18next'

import { AutoAwesome } from '@mui/icons-material'
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { useDisplayExperimentalFeature, ExperimentalFeature } from '../../../../hooks/useDisplayExperimentalFeature'
import { LiveEventGameAIPrompt } from '../../types/LiveEventGameAIPrompt'
import { TrackedGame } from '../../types/TrackedGame'
import { LiveEventGameAIButtonDialog, LiveEventGameAIButtonDialogData } from './LiveEventGameAIButtonDialog/LiveEventGameAIButtonDialog'
import { LiveEventGameAIButtonPromptDialog, LiveEventGameAIButtonPromptDialogData } from './LiveEventGameAIButtonPromptDialog/LiveEventGameAIButtonPromptDialog'

interface Props {
  trackedGames: TrackedGame[]
  selectedTrackedGame: TrackedGame
}

const LiveEventGameAIButton: React.FC<Props> = ({ trackedGames, selectedTrackedGame }) => {
  const displayExperimentalFeatureLiveEventAICustomPrompt = useDisplayExperimentalFeature(ExperimentalFeature.LiveEventAICustomPrompt)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)
  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const [liveEventGameAIButtonDialogData, setLiveEventGameAIButtonDialogData] = useState<LiveEventGameAIButtonDialogData | undefined>(undefined)
  const [liveEventGameAIButtonPromptDialogData, setLiveEventGameAIButtonPromptDialogData] = useState<LiveEventGameAIButtonPromptDialogData | undefined>(
    undefined
  )

  const liveEventGameAIPrompts = useMemo(() => {
    let prompts = [LiveEventGameAIPrompt.GAME_STRATEGY_CHANGES_14]

    if (displayExperimentalFeatureLiveEventAICustomPrompt) {
      prompts = [...prompts, LiveEventGameAIPrompt.GAME_CUSTOM_PROMPT]
    }

    return prompts
  }, [displayExperimentalFeatureLiveEventAICustomPrompt])

  const onButtonClick = useCallback(
    (selectedPrompt: LiveEventGameAIPrompt) => {
      if (selectedPrompt === LiveEventGameAIPrompt.GAME_CUSTOM_PROMPT) {
        setLiveEventGameAIButtonPromptDialogData({ selectedTrackedGame, trackedGames })
      } else {
        setLiveEventGameAIButtonDialogData({ selectedTrackedGame, selectedPrompt, trackedGames, liveEventGameAIPrompts })
      }

      handleMenuClose()
    },
    [selectedTrackedGame, trackedGames, liveEventGameAIPrompts]
  )

  const menuItems = useMemo(() => {
    return liveEventGameAIPrompts.map((key) => {
      return (
        <MenuItem
          onClick={() => onButtonClick(key)}
          key={key}
          disabled={key !== LiveEventGameAIPrompt.GAME_CUSTOM_PROMPT && !selectedTrackedGame.aiSummariesAvailable?.includes(key)}
        >
          <ListItemIcon>
            <AutoAwesome />
          </ListItemIcon>
          <ListItemText>
            <Trans i18nKey={`live-events:ai_button_${key}`} />
          </ListItemText>
        </MenuItem>
      )
    })
  }, [onButtonClick, liveEventGameAIPrompts, selectedTrackedGame.aiSummariesAvailable])

  return (
    <div className="LiveEventGameAIButton">
      <GRTooltip
        content={
          selectedTrackedGame.aiSummariesAvailable?.length === 0 && !displayExperimentalFeatureLiveEventAICustomPrompt ? (
            <Trans i18nKey={'live-events:ai_button_disabled_tooltip'} />
          ) : undefined
        }
      >
        <Button
          color="secondary"
          variant="contained"
          size="small"
          startIcon={<AutoAwesome />}
          onClick={handleMenuClick}
          disabled={selectedTrackedGame.aiSummariesAvailable?.length === 0 && !displayExperimentalFeatureLiveEventAICustomPrompt}
        >
          <Trans i18nKey="common:ai_text" />
        </Button>
      </GRTooltip>

      {liveEventGameAIButtonDialogData && (
        <LiveEventGameAIButtonDialog
          open={liveEventGameAIButtonDialogData ? true : false}
          data={liveEventGameAIButtonDialogData}
          onClose={() => setLiveEventGameAIButtonDialogData(undefined)}
        />
      )}

      {liveEventGameAIButtonPromptDialogData && (
        <LiveEventGameAIButtonPromptDialog
          open={liveEventGameAIButtonPromptDialogData ? true : false}
          data={liveEventGameAIButtonPromptDialogData}
          onClose={() => setLiveEventGameAIButtonPromptDialogData(undefined)}
        />
      )}

      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
        {menuItems}
      </Menu>
    </div>
  )
}

export default LiveEventGameAIButton
